import { Box } from '@chakra-ui/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ContactPlatform } from '../contact-drawer-url-state';
import type { ContactShell } from '../contact-types';
import type { ContactView } from '../contact-views';
import ContactNavRow from './ContactNavRow';
import ContactSection from './ContactSection';
import { useRovingTabIndex } from './use-roving-tabindex';

/** YouTube brand red for the coming-soon tile. Off-palette on purpose: a brand mark, like the IG/TikTok tiles. */
const YOUTUBE_BRAND_COLOR = '#FF0000';

interface ContactProfilesListProps {
  contact: ContactShell;
  view: ContactView;
  platform: ContactPlatform;
  /** Select a platform profile (sets view=profile + platform). */
  onSelectProfile: (platform: ContactPlatform) => void;
}

/**
 * PROFILS rail section. One `ContactNavRow` per connected platform (IG, TikTok).
 * Selecting a row drills the right panel into the profile view for that platform.
 * When the identity has no linked profile yet, an empty line is shown instead.
 * A disabled "YouTube · coming soon" row closes the list: no YouTube profile is
 * ever read, so it carries no handle or figure, only the roadmap.
 *
 * Rows form a roving-tabindex single-select list (`role="list"`): one Tab stop,
 * Arrow keys move between rows.
 */
const ContactProfilesList: React.FC<ContactProfilesListProps> = ({
  contact,
  view,
  platform,
  onSelectProfile,
}) => {
  const { t } = useTranslation();

  const rovingItems = useMemo(
    () => contact.profiles.map(() => ({ disabled: false })),
    [contact.profiles]
  );
  const { getRowProps } = useRovingTabIndex(rovingItems);

  return (
    <ContactSection label={t('contactDrawer.rail.profiles')}>
      {contact.profiles.length === 0 && (
        <Box fontFamily="body" fontSize="13px" color="primary.500" py="1" mb="6px">
          {t('contactDrawer.rail.profilesEmpty')}
        </Box>
      )}
      <Box role="list" display="flex" flexDirection="column" gap="6px">
        {contact.profiles.map((profile, index) => {
          const { ref, tabIndex, onKeyDown } = getRowProps(index);
          return (
            <ContactNavRow
              key={profile.platform}
              ref={ref}
              tabIndex={tabIndex}
              onKeyDown={onKeyDown}
              icon={{
                kind: 'platform',
                platform: profile.platform === 'ig' ? 'instagram' : 'tiktok',
                brandColor: profile.brandColor,
              }}
              title={profile.handle ? `@${profile.handle}` : t('planLocks.handle')}
              sublabel={profile.followersLabel}
              selected={view === 'profile' && platform === profile.platform}
              onSelect={() => onSelectProfile(profile.platform)}
            />
          );
        })}
        <ContactNavRow
          icon={{ kind: 'platform', platform: 'youtube', brandColor: YOUTUBE_BRAND_COLOR }}
          title={t('contactDrawer.rail.youtubeSoon')}
          disabled
        />
      </Box>
    </ContactSection>
  );
};

export default ContactProfilesList;
